import { useState, useEffect } from "react";
import { Plus, UtensilsCrossed } from "lucide-react";
import { money, fileUrl } from "@/lib/api";
import ProductModal from "@/components/ProductModal";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";

export default function MenuItemCard({ item, autoOpen = false }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [flash, setFlash] = useState(false);
  const { addItem } = useCart();
  const soldOut = !item.available;
  const hasOptions = (item.option_groups || []).length > 0;
  const img = fileUrl(item.image_url);

  useEffect(() => {
    if (!autoOpen) return;
    if (hasOptions && !soldOut) {
      setModalOpen(true);
    } else {
      setFlash(true);
      const t = setTimeout(() => setFlash(false), 2500);
      return () => clearTimeout(t);
    }
  }, [autoOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAdd = () => {
    if (soldOut) return;
    if (hasOptions) {
      setModalOpen(true);
      return;
    }
    addItem({
      item_id: item.id,
      name: item.name,
      base_price: item.base_price,
      unit_price: item.base_price,
      quantity: 1,
      selected_options: [],
      special_instructions: "",
    });
    toast.success(`${item.name} added to cart`);
  };

  return (
    <>
      <div
        data-testid={`menu-item-card-${item.id}`}
        id={`item-${item.id}`}
        className={`group relative flex flex-col overflow-hidden rounded-2xl border bg-white shadow-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-lg ${
          flash ? "border-orange-500 ring-2 ring-orange-400" : "border-stone-200"
        } ${soldOut ? "opacity-80" : ""}`}
      >
        {/* Image / placeholder */}
        <div className="relative h-44 w-full overflow-hidden bg-gradient-to-br from-amber-100 via-orange-100 to-stone-200">
          {img ? (
            <img src={img} alt={item.name} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-orange-300">
              <UtensilsCrossed className="h-12 w-12" />
            </div>
          )}
          {soldOut && (
            <div className="absolute inset-0 flex items-center justify-center bg-stone-900/40">
              <span data-testid={`sold-out-badge-${item.id}`} className="rounded-full border border-rose-300 bg-rose-100 px-4 py-1.5 text-sm font-bold text-rose-800">
                Sold Out
              </span>
            </div>
          )}
          {!soldOut && (
            <span className="absolute left-3 top-3 rounded-full border border-emerald-300 bg-emerald-100/95 px-3 py-1 text-xs font-semibold text-emerald-800">
              Available
            </span>
          )}
        </div>

        <div className="flex flex-1 flex-col p-5">
          <div className="flex items-start justify-between gap-3">
            <h3 className="font-display text-xl font-bold text-stone-900">{item.name}</h3>
            <span className="whitespace-nowrap font-display text-lg font-bold text-orange-700">{money(item.base_price)}</span>
          </div>
          <p className="mt-2 flex-1 text-sm leading-relaxed text-stone-600">{item.description}</p>

          {hasOptions && !soldOut && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {item.option_groups.map((g) => (
                <span key={g.name} className="rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-800">{g.name}</span>
              ))}
            </div>
          )}

          <div className="mt-4 flex items-center justify-end">
            <button
              data-testid={`menu-item-add-button-${item.id}`}
              onClick={handleAdd}
              disabled={soldOut}
              className="flex items-center gap-1.5 rounded-full bg-orange-700 px-5 py-2.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-orange-800 disabled:cursor-not-allowed disabled:bg-stone-300 disabled:text-stone-500"
            >
              <Plus className="h-4 w-4" />
              {hasOptions ? "Customize" : "Add to Cart"}
            </button>
          </div>
        </div>
      </div>

      {hasOptions && <ProductModal item={item} open={modalOpen} onOpenChange={setModalOpen} />}
    </>
  );
}
